import React, { FC, useEffect, useRef, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { CheckIcon, ClockIcon } from "@heroicons/react/24/outline";
import {
  clock,
  isOutsideOperationalHoursOn,
  isTimeBooked,
  timeSlots,
  windowForDate,
} from "@1-now/booking-core";
import { useData } from "data/data-provider";
import { branchHoursText } from "./availability";
import PickerLayer from "./PickerLayer";
import TripTile from "./TripTile";
import { useIsSheet } from "./useIsSheet";

type End = "start" | "end";
/** open: can be picked. booked: the car is out then. closed: the branch is shut then. */
export type SlotState = "open" | "booked" | "closed";

export interface TimePickerBProps {
  /** `tiles`: a pick-up and a drop-off time field. `chip`: the compact trigger of the scrolled header bar. */
  variant?: "tiles" | "chip";
  desktopClassName?: string;
}

const ROW = 44; // the wheel's row height, px
const VIEW = 220; // the wheel's height, px

/**
 * 1NOW-11176: the times field of the Design B car page. Two fields (pick-up time, return time) open one
 * surface with a tab for each end, the branch's opening times above the choices, and the choices: a grid of
 * 30-minute steps on desktop (the pick-up time list of sixt.com), a wheel on phones. Times the branch is
 * shut at, and times this car is out (a booking's window on that day), are disabled and greyed, by the same
 * two rules the times field in the other designs uses (isOutsideOperationalHoursOn, isTimeBooked).
 *
 * It only draws and sets `startTime` / `endTime`. What follows from a time (the return time following the
 * pick-up, a time moved off a closed hour, the quote) still happens in RentalCarTimesRangeInput's effects,
 * which the car page keeps mounted beside it.
 */
const TimePickerB: FC<TimePickerBProps> = ({
  variant = "tiles",
  desktopClassName = "absolute left-0 top-full mt-2 w-[min(420px,calc(100vw-2rem))]",
}) => {
  const {
    startDate,
    endDate,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    setEndTimeTouched,
    pickupLocation,
    dropOffLocation,
    dropoffLocationType,
    partiallyUnavailableTimes,
    ourCompany,
  } = useData();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const sheet = useIsSheet();
  const [open, setOpen] = useState(false);
  const [which, setWhich] = useState<End>("start");

  const returnBranch = dropoffLocationType === false && dropOffLocation ? dropOffLocation : pickupLocation;
  const dateOf = (end: End) => (end === "start" ? startDate : endDate) ?? null;
  const branchOf = (end: End) => (end === "start" ? pickupLocation : returnBranch);
  const timeOf = (end: End) => (end === "start" ? startTime : endTime);

  const windows = (date: Date | null) => {
    if (!date || !moment(date).isValid()) return [];
    const found = (partiallyUnavailableTimes as any)?.[moment(date).format("YYYY-MM-DD")];
    return Array.isArray(found) ? found : [];
  };
  const slotState = (time: string, end: End): SlotState => {
    const date = dateOf(end);
    if (isTimeBooked(time, windows(date), end)) return "booked";
    if (isOutsideOperationalHoursOn(time, branchOf(end), date)) return "closed";
    return "open";
  };

  const pick = (time: string, end: End) => {
    const state = slotState(time, end);
    if (state === "booked") {
      toast.error("This time slot is unavailable due to a partial booking");
      return;
    }
    if (state === "closed") {
      toast.error("This time is outside the location's operational hours");
      return;
    }
    if (end === "end") {
      setEndTimeTouched?.(true);
      setEndTime(time);
    } else {
      setStartTime(time);
    }
  };

  const shown = (time: string | undefined) => (time ? clock(time, ourCompany) : "Add time");
  const dayClosed = (end: End) => windowForDate(branchOf(end), dateOf(end)) === null;
  const closedCaption = (end: End) => `Closed on ${moment(dateOf(end)).format("dddd")}. Choose another date.`;

  // The branch's opening times for the end on show, on that end's day.
  const hours = branchHoursText(branchOf(which), dateOf(which), ourCompany);
  const note = dayClosed(which) ? closedCaption(which) : hours ? `Opening times: ${hours}` : null;

  // ----- desktop: a grid of 30-minute steps ----------------------------------------------------------
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open || sheet) return;
    const grid = gridRef.current;
    const row = grid?.querySelector<HTMLElement>('[aria-current="true"]');
    if (grid && row) grid.scrollTop = row.offsetTop - grid.clientHeight / 2 + row.clientHeight / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sheet, which]);

  const grid = (
    <div
      ref={gridRef}
      data-testid={`time-list-${which}`}
      className="cpb-hide-scrollbar relative grid max-h-[272px] grid-cols-2 gap-2 overflow-y-auto overscroll-contain"
    >
      {timeSlots.map((time) => {
        const state = slotState(time, which);
        const selected = timeOf(which) === time;
        return (
          <button
            key={time}
            type="button"
            aria-disabled={state !== "open"}
            aria-current={selected ? "true" : undefined}
            data-state={state}
            onClick={() => pick(time, which)}
            className={`flex h-11 items-center justify-center gap-1.5 rounded-lg text-sm transition-colors ${
              selected
                ? "bg-primary-brand font-medium text-on-primary"
                : state === "open"
                  ? "bg-text-color/[0.05] text-text-color hover:bg-text-color/10"
                  : "cursor-not-allowed text-text-color/30"
            }`}
          >
            {clock(time, ourCompany)}
            {selected && <CheckIcon className="h-4 w-4" />}
          </button>
        );
      })}
    </div>
  );

  // ----- phones: a wheel ------------------------------------------------------------------------------
  const wheelRef = useRef<HTMLDivElement>(null);
  const touched = useRef(false);
  const settle = useRef<ReturnType<typeof setTimeout>>();
  const current = timeOf(which);

  // Put the wheel on the chosen time when the sheet opens and when the tab changes. That scroll is ours,
  // not the renter's: only a scroll they start is allowed to change the time (touched).
  useEffect(() => {
    if (!open || !sheet) return;
    touched.current = false;
    const at = Math.max(0, timeSlots.indexOf(current));
    if (wheelRef.current) wheelRef.current.scrollTop = at * ROW;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sheet, which]);
  useEffect(() => () => clearTimeout(settle.current), []);

  const onWheelScroll = () => {
    if (!touched.current) return;
    clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      const el = wheelRef.current;
      if (!el) return;
      const index = Math.min(timeSlots.length - 1, Math.max(0, Math.round(el.scrollTop / ROW)));
      if (slotState(timeSlots[index], which) === "open") {
        if (timeSlots[index] !== current) pick(timeSlots[index], which);
        return;
      }
      // Landed on a time that cannot be picked: slide to the nearest one that can.
      for (let d = 1; d < timeSlots.length; d++) {
        const next = [index - d, index + d].find(
          (i) => i >= 0 && i < timeSlots.length && slotState(timeSlots[i], which) === "open",
        );
        if (next !== undefined) {
          el.scrollTo({ top: next * ROW, behavior: "smooth" });
          return;
        }
      }
    }, 120);
  };

  const wheel = (
    <div className="relative" style={{ height: VIEW }} data-testid="time-wheel">
      <div
        className="pointer-events-none absolute inset-x-0 border-y border-text-color/20"
        style={{ top: (VIEW - ROW) / 2, height: ROW }}
      />
      <div
        ref={wheelRef}
        onScroll={onWheelScroll}
        onPointerDown={() => (touched.current = true)}
        onTouchStart={() => (touched.current = true)}
        onWheel={() => (touched.current = true)}
        className="cpb-hide-scrollbar relative h-full snap-y snap-mandatory overflow-y-auto overscroll-contain"
        style={{ paddingTop: (VIEW - ROW) / 2, paddingBottom: (VIEW - ROW) / 2 }}
      >
        {timeSlots.map((time) => {
          const state = slotState(time, which);
          return (
            <div
              key={time}
              data-state={state}
              aria-disabled={state !== "open"}
              aria-current={current === time ? "true" : undefined}
              onClick={() => pick(time, which)}
              className={`flex snap-center items-center justify-center text-lg ${
                state === "open" ? "font-medium text-text-color" : "text-text-color/25"
              }`}
              style={{ height: ROW }}
            >
              {clock(time, ourCompany)}
            </div>
          );
        })}
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-card-color to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-card-color to-transparent" />
    </div>
  );

  const footer = (
    <div className="md:flex md:justify-end">
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="h-11 w-full rounded-lg bg-primary-brand text-sm font-semibold text-on-primary transition-colors hover:bg-primary-700 md:w-auto md:px-8"
      >
        Save time
      </button>
    </div>
  );

  const openAt = (end: End) => {
    setWhich(end);
    setOpen((was) => !(was && which === end));
  };

  return (
    <div ref={wrapperRef} className="relative" data-field="times">
      {variant === "chip" ? (
        <TripTile
          layout="chip"
          icon={<ClockIcon className="h-4 w-4" />}
          label="Times"
          value={`${shown(startTime)} – ${shown(endTime)}`}
          active={open}
          onClick={() => openAt("start")}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <TripTile
            icon={<ClockIcon className="h-5 w-5" />}
            label="Pick-up time"
            value={shown(startTime)}
            active={open && which === "start"}
            onClick={() => openAt("start")}
          />
          <TripTile
            icon={<ClockIcon className="h-5 w-5" />}
            label="Drop-off time"
            value={shown(endTime)}
            active={open && which === "end"}
            onClick={() => openAt("end")}
          />
        </div>
      )}
      <PickerLayer
        open={open}
        onClose={() => setOpen(false)}
        title="Select time"
        wrapperRef={wrapperRef}
        desktopClassName={desktopClassName}
        footer={footer}
        name="times"
      >
        <div role="tablist" aria-label="Which time" className="mb-3 grid grid-cols-2 border-b border-text-color/10">
          {(["start", "end"] as End[]).map((end) => (
            <button
              key={end}
              type="button"
              role="tab"
              aria-selected={which === end}
              onClick={() => setWhich(end)}
              className={`-mb-px border-b-2 px-2 pb-2 pt-1 text-left text-sm ${
                which === end ? "border-primary-brand font-medium text-text-color" : "border-transparent text-text-color/60"
              }`}
            >
              <span className="block text-xs">{end === "start" ? "Pick-up" : "Return"}</span>
              <span className="block text-[15px]">{shown(timeOf(end))}</span>
            </button>
          ))}
        </div>
        {note && (
          <p className="mb-3 flex items-center gap-2 text-[13px] text-text-color/70" data-testid="time-hours-note">
            <ClockIcon className="h-4 w-4 shrink-0" />
            {note}
          </p>
        )}
        {sheet ? wheel : grid}
      </PickerLayer>
    </div>
  );
};

export default TimePickerB;
